import { supabase } from '@/integrations/supabase/client';

export interface TrustScoreCalculation {
  total_playtime_ratio: number;
  successful_covers: number;
  total_keys: number;
  reports: number;
  calculated_score: number;
}

// Calculer le trust score d'un joueur
export async function calculateTrustScore(playerId: string): Promise<TrustScoreCalculation | null> {
  try {
    // Récupérer les données de temps de jeu
    const { data: playtimeData, error: playtimeError } = await supabase
      .from('playtime_tracking')
      .select('playtime_ratio, required_playtime')
      .eq('player_id', playerId);

    if (playtimeError) {
      console.error('Error fetching playtime data:', playtimeError);
      return null;
    }

    // Calculer le ratio moyen de temps de jeu
    const totalPlaytimeRatio = playtimeData?.length > 0 
      ? playtimeData.reduce((sum, item) => sum + (item.playtime_ratio || 0), 0) / playtimeData.length 
      : 0;

    // Récupérer les clés obtenues et les couvertures réussies
    const { data: keyRequestsData, error: keyRequestsError } = await supabase
      .from('key_requests')
      .select(`
        id,
        status,
        is_covered
      `)
      .eq('creator_id', playerId)
      .eq('status', 'approved');

    if (keyRequestsError) {
      console.error('Error fetching key requests:', keyRequestsError);
      return null;
    }

    const totalKeys = keyRequestsData?.length || 0;
    const successfulCovers = keyRequestsData?.filter(item => item.is_covered).length || 0;

    // Récupérer les signalements reçus
    const { data: reportsData, error: reportsError } = await supabase
      .from('reports')
      .select('id')
      .eq('reported_user_id', playerId);

    if (reportsError) {
      console.error('Error fetching reports:', reportsError);
      return null;
    }

    const reports = reportsData?.length || 0;

    // Calculer le trust score selon la formule
    // trust_score = (total_playtime_ratio * 0.7) + (successful_covers / total_keys * 0.3) - (reports * 2)
    const playtimeComponent = totalPlaytimeRatio * 0.7;
    const coverageComponent = totalKeys > 0 ? (successfulCovers / totalKeys) * 0.3 : 0;
    const reportsPenalty = reports * 2;
    
    const calculatedScore = Math.max(0, Math.min(100, 
      (playtimeComponent + coverageComponent - reportsPenalty) * 100
    ));

    return {
      total_playtime_ratio: totalPlaytimeRatio,
      successful_covers: successfulCovers,
      total_keys: totalKeys,
      reports: reports,
      calculated_score: Math.round(calculatedScore)
    };
  } catch (error) {
    console.error('Error calculating trust score:', error);
    return null;
  }
}

// Mettre à jour le trust score d'un joueur
export async function updateTrustScore(playerId: string): Promise<boolean> {
  try {
    const calculation = await calculateTrustScore(playerId);
    
    if (!calculation) {
      console.error('Could not calculate trust score');
      return false;
    }

    // Mettre à jour le trust score dans la base de données
    const { error } = await supabase
      .from('profiles')
      .update({ trust_score: calculation.calculated_score })
      .eq('id', playerId);

    if (error) {
      console.error('Error updating trust score:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating trust score:', error);
    return false;
  }
}

// Mettre à jour le temps de jeu depuis Steam
export async function updatePlaytimeFromSteam(playerId: string, gameId: string, keyRequestId: string): Promise<boolean> {
  try {
    // Récupérer les informations du jeu
    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select('steam_url, required_playtime')
      .eq('id', gameId)
      .single();

    if (gameError || !gameData) {
      console.error('Error fetching game data:', gameError);
      return false;
    }

    // TODO: Intégrer l'API Steam pour récupérer le temps de jeu réel
    // Pour l'instant, on simule avec des données de test
    const steamPlaytime = await getSteamPlaytime(playerId, gameData.steam_url);
    const requiredPlaytime = gameData.required_playtime || 60; // 1 heure par défaut
    const playtimeRatio = Math.min(steamPlaytime / requiredPlaytime, 1);

    // Mettre à jour ou créer l'entrée de suivi du temps de jeu
    const { error: upsertError } = await supabase
      .from('playtime_tracking')
      .upsert({
        player_id: playerId,
        game_id: gameId,
        key_request_id: keyRequestId,
        steam_playtime: steamPlaytime,
        required_playtime: requiredPlaytime,
        playtime_ratio: playtimeRatio,
        last_updated: new Date().toISOString()
      });

    if (upsertError) {
      console.error('Error updating playtime tracking:', upsertError);
      return false;
    }

    // Mettre à jour le trust score
    await updateTrustScore(playerId);

    return true;
  } catch (error) {
    console.error('Error updating playtime from Steam:', error);
    return false;
  }
}

// Récupérer le temps de jeu depuis Steam
async function getSteamPlaytime(playerId: string, steamUrl: string): Promise<number> {
  try {
    // Récupérer le Steam ID du joueur depuis la base de données
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('steam_id')
      .eq('id', playerId)
      .single();

    if (profileError || !profile?.steam_id) {
      return Math.floor(Math.random() * 120); // Simulation de fallback
    }

    // Utiliser le service Steam pour récupérer le temps de jeu réel
    const { updatePlaytimeFromSteam } = await import('./steamService');
    const appId = extractSteamAppId(steamUrl);
    
    if (!appId) {
      return Math.floor(Math.random() * 120); // Simulation de fallback
    }

    // Pour l'instant, utiliser la simulation jusqu'à ce que l'API Steam soit configurée
    return Math.floor(Math.random() * 120);
  } catch (error) {
    console.error('Error getting Steam playtime:', error);
    return Math.floor(Math.random() * 120); // Simulation de fallback
  }
}

// Fonction utilitaire pour extraire l'App ID Steam
function extractSteamAppId(steamUrl: string): number | null {
  try {
    const patterns = [
      /store\.steampowered\.com\/app\/(\d+)/,
      /steamcommunity\.com\/app\/(\d+)/,
      /steam:\/\/store\/(\d+)/
    ];

    for (const pattern of patterns) {
      const match = steamUrl.match(pattern);
      if (match) {
        return parseInt(match[1], 10);
      }
    }

    return null;
  } catch (error) {
    console.error('Error extracting Steam App ID:', error);
    return null;
  }
}

// Récupérer l'historique du temps de jeu d'un joueur
export async function getPlaytimeHistory(playerId: string) {
  const { data, error } = await supabase
    .from('playtime_tracking')
    .select(`
      *,
      game:games(title, cover_image_url, steam_url)
    `)
    .eq('player_id', playerId)
    .order('last_updated', { ascending: false });

  if (error) {
    console.error('Error fetching playtime history:', error);
    return [];
  }

  return data || [];
}

// Vérifier si un joueur peut demander un jeu (score minimum requis)
export async function canPlayerRequestGame(playerId: string, gameId: string): Promise<boolean> {
  try {
    // Récupérer le trust score du joueur
    const { data: playerData, error: playerError } = await supabase
      .from('profiles')
      .select('trust_score')
      .eq('id', playerId)
      .single();

    if (playerError || !playerData) {
      console.error('Error fetching player data:', playerError);
      return false;
    }

    // Récupérer le score minimum requis pour le jeu
    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select('min_trust_score')
      .eq('id', gameId)
      .single();

    if (gameError || !gameData) {
      console.error('Error fetching game data:', gameError);
      return false;
    }

    const minRequiredScore = gameData.min_trust_score || 0;
    return playerData.trust_score >= minRequiredScore;
  } catch (error) {
    console.error('Error checking if player can request game:', error);
    return false;
  }
}
